import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { inventoryAPI, projectAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  Building2, Home, CheckCircle2, XCircle, Lock, Phone,
  Layers, IndianRupee, Ruler, ChevronRight, Eye, EyeOff,
  ArrowLeft, Compass, Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SalesInventoryView = () => {
  const { user, hasRole } = useAuth();
  const [searchParams] = useSearchParams();
  const projectFromUrl = searchParams.get('project');
  
  const [units, setUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inventorySettings, setInventorySettings] = useState(null);
  
  // Guided filter state - Step by step
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedProject, setSelectedProject] = useState(projectFromUrl || '');
  const [selectedBhk, setSelectedBhk] = useState('');
  const [selectedSeries, setSelectedSeries] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState('');
  
  // View state
  const [showDetails, setShowDetails] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [revealedUnits, setRevealedUnits] = useState(new Set());

  const isBuilder = hasRole(['builder_admin', 'super_admin']);

  useEffect(() => {
    loadData();
    loadSettings();
  }, []);

  useEffect(() => {
    if (projectFromUrl) {
      setSelectedProject(projectFromUrl);
      setCurrentStep(2);
    }
  }, [projectFromUrl]);

  const loadData = async () => {
    try {
      const [unitsRes, projectsRes] = await Promise.all([
        inventoryAPI.list(),
        projectAPI.list(),
      ]);
      setUnits(unitsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/inventory/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInventorySettings(data);
      } else {
        // Default settings if none exist
        setInventorySettings({
          floor_visibility: 'all', // 'all', 'limited'
          floor_limit: 5,
          availability_display: 'show_all', // 'show_all', 'show_on_click', 'call_enquire'
          pricing_visibility: true,
        });
      }
    } catch (error) {
      // Default settings
      setInventorySettings({
        floor_visibility: 'all',
        floor_limit: 5,
        availability_display: 'show_all',
        pricing_visibility: true,
      });
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'Price on request';
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString()}`;
  };

  const getStatusBadge = (status, unitId) => {
    const settings = inventorySettings || {};
    
    // If "call_enquire" mode, don't show status
    if (!isBuilder && settings.availability_display === 'call_enquire') {
      return (
        <Badge className="bg-blue-100 text-blue-700">
          <Phone className="h-3 w-3 mr-1" />
          Call for Status
        </Badge>
      );
    }
    
    // If "show_on_click" mode and not revealed
    if (!isBuilder && settings.availability_display === 'show_on_click' && !revealedUnits.has(unitId)) {
      return (
        <Button 
          variant="outline" 
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setRevealedUnits(new Set([...revealedUnits, unitId]));
          }}
        >
          <Eye className="h-3 w-3 mr-1" />
          Click to reveal
        </Button>
      );
    }
    
    const styles = {
      'available': { class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2, label: 'Available' },
      'blocked': { class: 'bg-amber-100 text-amber-700', icon: Lock, label: 'Blocked' },
      'sold': { class: 'bg-red-100 text-red-700', icon: XCircle, label: 'Sold' },
    };
    const style = styles[status] || styles.available;
    const Icon = style.icon;
    return (
      <Badge className={style.class}>
        <Icon className="h-3 w-3 mr-1" />
        {style.label}
      </Badge>
    );
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : 'Unknown';
  };

  // Filter units based on selections
  const projectUnits = units.filter(u => u.project_id === selectedProject);
  const bhkTypes = [...new Set(projectUnits.map(u => u.bhk_type).filter(Boolean))].sort();
  
  // Get series for selected BHK
  const getSeriesForBhk = () => {
    const bhkUnits = projectUnits.filter(u => u.bhk_type === selectedBhk);
    const series = [...new Set(bhkUnits.map(u => {
      const unitNum = u.unit_number?.toString() || '';
      return unitNum.slice(-2);
    }))].filter(s => s).sort();
    return series;
  };
  
  // Get floors based on selections
  const getAvailableFloors = () => {
    let filtered = projectUnits.filter(u => u.bhk_type === selectedBhk);
    if (selectedSeries.length > 0) {
      filtered = filtered.filter(u => {
        const unitNum = u.unit_number?.toString() || '';
        const series = unitNum.slice(-2);
        return selectedSeries.includes(series);
      });
    }
    const floors = [...new Set(filtered.map(u => u.floor).filter(f => f !== null && f !== undefined))].sort((a, b) => b - a);
    
    // Apply floor limit if settings say so
    const settings = inventorySettings || {};
    if (!isBuilder && settings.floor_visibility === 'limited' && settings.floor_limit) {
      return floors.slice(0, settings.floor_limit);
    }
    return floors;
  };
  
  // Get units for the selected floor
  const getFloorUnits = () => {
    let filtered = projectUnits.filter(u => 
      u.bhk_type === selectedBhk && 
      u.floor === parseInt(selectedFloor)
    );
    if (selectedSeries.length > 0) {
      filtered = filtered.filter(u => {
        const unitNum = u.unit_number?.toString() || '';
        const series = unitNum.slice(-2);
        return selectedSeries.includes(series);
      });
    }
    return filtered.sort((a, b) => {
      const aNum = parseInt(a.unit_number) || 0;
      const bNum = parseInt(b.unit_number) || 0;
      return aNum - bNum;
    });
  };

  const series = getSeriesForBhk();
  const floors = getAvailableFloors();
  const floorUnits = getFloorUnits();

  const handleBack = () => {
    if (currentStep === 2) {
      setSelectedProject('');
      setCurrentStep(1);
    } else if (currentStep === 3) {
      setSelectedBhk('');
      setSelectedSeries([]);
      setCurrentStep(2);
    } else if (currentStep === 4) {
      setSelectedSeries([]);
      setCurrentStep(3);
    } else if (currentStep === 5) {
      setSelectedFloor('');
      setCurrentStep(4);
    }
  };

  const toggleSeries = (s) => {
    if (selectedSeries.includes(s)) {
      setSelectedSeries(selectedSeries.filter(x => x !== s));
    } else {
      setSelectedSeries([...selectedSeries, s]);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-inventory-view">
      {/* Header with Progress */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <Layers className="h-7 w-7 text-amber-600" />
              Inventory Explorer
            </h1>
            <p className="text-muted-foreground mt-1">
              Select options to find available units
            </p>
          </div>
          {currentStep > 1 && (
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
        </div>
        
        {/* Progress Steps */}
        <div className="flex items-center gap-2 text-sm overflow-x-auto pb-2">
          <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${currentStep >= 1 ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'}`}>
            <span className="font-medium">1</span>
            <span>Project</span>
            {selectedProject && <span className="font-medium">: {getProjectName(selectedProject)}</span>}
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${currentStep >= 2 ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'}`}>
            <span className="font-medium">2</span>
            <span>BHK</span>
            {selectedBhk && <span className="font-medium">: {selectedBhk}</span>}
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${currentStep >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'}`}>
            <span className="font-medium">3</span>
            <span>Series</span>
            {selectedSeries.length > 0 && <span className="font-medium">: {selectedSeries.join(', ')}</span>}
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${currentStep >= 4 ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'}`}>
            <span className="font-medium">4</span>
            <span>Floor</span>
            {selectedFloor && <span className="font-medium">: {selectedFloor}</span>}
          </div>
        </div>
      </div>

      {/* Step 1: Select Project */}
      {currentStep === 1 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Select a Project</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => (
              <Card 
                key={project.id}
                className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-amber-500"
                onClick={() => {
                  setSelectedProject(project.id);
                  setCurrentStep(2);
                }}
                data-testid={`project-card-${project.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <Building2 className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{project.name}</h3>
                      <p className="text-sm text-muted-foreground">{project.location}</p>
                      <p className="text-sm mt-2">
                        <span className="text-emerald-600 font-medium">
                          {units.filter(u => u.project_id === project.id && u.status === 'available').length}
                        </span>
                        <span className="text-muted-foreground"> units available</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Select BHK Type */}
      {currentStep === 2 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Select BHK Type</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {bhkTypes.map((bhk) => {
              const bhkUnits = projectUnits.filter(u => u.bhk_type === bhk);
              const availableCount = bhkUnits.filter(u => u.status === 'available').length;
              return (
                <Card 
                  key={bhk}
                  className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-amber-500"
                  onClick={() => {
                    setSelectedBhk(bhk);
                    setCurrentStep(3);
                  }}
                  data-testid={`bhk-card-${bhk}`}
                >
                  <CardContent className="p-4 text-center">
                    <Home className="h-8 w-8 mx-auto text-amber-600 mb-2" />
                    <h3 className="text-xl font-bold">{bhk}</h3>
                    <p className="text-sm text-muted-foreground">{bhkUnits.length} units</p>
                    <Badge className="mt-2 bg-emerald-100 text-emerald-700">
                      {availableCount} available
                    </Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 3: Select Series */}
      {currentStep === 3 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Select Flat Series</h2>
              <p className="text-sm text-muted-foreground">Select one or more series (flat endings)</p>
            </div>
            {selectedSeries.length > 0 && (
              <Button onClick={() => setCurrentStep(4)}>
                Continue with {selectedSeries.length} series
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {series.map((s) => {
              const seriesUnits = projectUnits.filter(u => {
                const unitNum = u.unit_number?.toString() || '';
                return u.bhk_type === selectedBhk && unitNum.slice(-2) === s;
              });
              const availableCount = seriesUnits.filter(u => u.status === 'available').length;
              const isSelected = selectedSeries.includes(s);
              return (
                <Card 
                  key={s}
                  className={`cursor-pointer hover:shadow-lg transition-all border-2 ${isSelected ? 'border-amber-500 bg-amber-50' : 'hover:border-amber-300'}`}
                  onClick={() => toggleSeries(s)}
                  data-testid={`series-card-${s}`}
                >
                  <CardContent className="p-3 text-center">
                    <h3 className="text-lg font-bold">{s} Series</h3>
                    <p className="text-xs text-muted-foreground">
                      {availableCount}/{seriesUnits.length} available
                    </p>
                    {isSelected && (
                      <CheckCircle2 className="h-5 w-5 mx-auto mt-2 text-amber-600" />
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
          {selectedSeries.length === 0 && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Click on one or more series to select, then continue
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Step 4: Select Floor */}
      {currentStep === 4 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Select Floor</h2>
          {!isBuilder && inventorySettings?.floor_visibility === 'limited' && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Showing top {inventorySettings.floor_limit} floors. Contact sales for more options.
              </AlertDescription>
            </Alert>
          )}
          <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
            {floors.map((floor) => {
              const floorUnitsForCount = projectUnits.filter(u => {
                if (u.bhk_type !== selectedBhk || u.floor !== floor) return false;
                if (selectedSeries.length > 0) {
                  const unitNum = u.unit_number?.toString() || '';
                  const series = unitNum.slice(-2);
                  return selectedSeries.includes(series);
                }
                return true;
              });
              const availableCount = floorUnitsForCount.filter(u => u.status === 'available').length;
              return (
                <Card 
                  key={floor}
                  className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-amber-500"
                  onClick={() => {
                    setSelectedFloor(floor.toString());
                    setCurrentStep(5);
                  }}
                  data-testid={`floor-card-${floor}`}
                >
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">Floor</p>
                    <h3 className="text-2xl font-bold">{floor}</h3>
                    <Badge className={`mt-1 ${availableCount > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {availableCount} avail
                    </Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 5: Show Units */}
      {currentStep === 5 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Floor {selectedFloor} - {selectedBhk} Units
            </h2>
            <Badge variant="outline" className="text-base px-3 py-1">
              {floorUnits.filter(u => u.status === 'available').length} / {floorUnits.length} Available
            </Badge>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {floorUnits.map((unit) => (
              <Card 
                key={unit.id}
                className={`cursor-pointer hover:shadow-lg transition-shadow border-2 ${
                  unit.status === 'available' ? 'border-emerald-200 hover:border-emerald-500' :
                  unit.status === 'blocked' ? 'border-amber-200' : 'border-red-200 opacity-60'
                }`}
                onClick={() => {
                  setSelectedUnit(unit);
                  setShowDetails(true);
                }}
                data-testid={`unit-card-${unit.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-xl font-bold">{unit.unit_number}</h3>
                    {getStatusBadge(unit.status, unit.id)}
                  </div>
                  
                  <div className="space-y-1 text-sm">
                    {unit.carpet_area && (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Ruler className="h-3 w-3" />
                        {unit.carpet_area} sq.ft
                      </div>
                    )}
                    {unit.door_facing && (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Compass className="h-3 w-3" />
                        {unit.door_facing.replace('_', '-')}
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-3 pt-3 border-t">
                    {(!isBuilder && inventorySettings?.pricing_visibility === false) ? (
                      <p className="text-muted-foreground text-sm flex items-center gap-1">
                        <EyeOff className="h-3 w-3" />
                        Price on request
                      </p>
                    ) : (
                      <p className="text-lg font-bold text-amber-600">
                        {formatPrice(unit.price)}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {floorUnits.length === 0 && (
            <Card className="p-8 text-center">
              <Home className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Units Found</h3>
              <p className="text-muted-foreground">
                No units match your selected criteria on this floor.
              </p>
            </Card>
          )}
        </div>
      )}

      {/* Unit Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Unit {selectedUnit?.unit_number}</DialogTitle>
          </DialogHeader>
          {selectedUnit && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Status</span>
                {getStatusBadge(selectedUnit.status, selectedUnit.id)}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Project</p>
                  <p className="font-medium">{getProjectName(selectedUnit.project_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Floor</p>
                  <p className="font-medium">{selectedUnit.floor}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">BHK Type</p>
                  <p className="font-medium">{selectedUnit.bhk_type}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Facing</p>
                  <p className="font-medium">{selectedUnit.door_facing?.replace('_', '-') || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Carpet Area</p>
                  <p className="font-medium">{selectedUnit.carpet_area || 'N/A'} sq.ft</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Saleable Area</p>
                  <p className="font-medium">{selectedUnit.saleable_area || 'N/A'} sq.ft</p>
                </div>
              </div>
              
              <div className="p-4 bg-amber-50 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Price</p>
                {(!isBuilder && inventorySettings?.pricing_visibility === false) ? (
                  <p className="text-lg font-medium flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Contact for pricing
                  </p>
                ) : (
                  <p className="text-2xl font-bold text-amber-600">{formatPrice(selectedUnit.price)}</p>
                )}
              </div>
              
              {selectedUnit.status === 'available' && (
                <DialogFooter>
                  <Button className="w-full">
                    <Phone className="h-4 w-4 mr-2" />
                    Contact Sales
                  </Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesInventoryView;
